import { useMemo } from 'react';
import { NumberInput, EnumDropdown, useFormContext } from 'framework/form';
import { get } from 'lodash';
import { TechnologyDropdown } from 'domain/shared/career';
import { EDUCATION_SPECIALIST_ID, SOFTWARE_ENGINEER_ID, PositionDropdown } from 'domain/shared/employee';
import { SIMPLE_QUALIFICATION } from 'domain/shared/presale';
import { PROJECT_MEMBERSHIP_ROLES } from 'domain/shared/core/constants';
import { COMPETENCY_LEVEL_WITHOUT_NA } from 'domain/shared/newVacancy';
import { TextInput } from 'framework/form';
import { Grid2 as Grid } from '@mui/material';

interface RequiredResourcesInputTemplateProps {
  source: string;
}

const LEVEL_ORDER = Object.keys(COMPETENCY_LEVEL_WITHOUT_NA);

const RequiredResourcesInputTemplate = ({ source, ...props }: RequiredResourcesInputTemplateProps) => {
  const { data } = useFormContext();

  const minLevel = get(data, `${source}.minLevel`);
  const maxLevel = get(data, `${source}.maxLevel`);

  const levelErrors = useMemo<Partial<Record<'minLevel' | 'maxLevel', string[]>>>(() => {
    if (!minLevel || !maxLevel) return {};

    const minIndex = LEVEL_ORDER.indexOf(minLevel);
    const maxIndex = LEVEL_ORDER.indexOf(maxLevel);

    if (minIndex !== -1 && maxIndex !== -1 && minIndex > maxIndex) {
      return {
        minLevel: ['Min Level must be less than or equal to Max Level'],
        maxLevel: ['Max Level must be greater than or equal to Min Level'],
      };
    }
    return {};
  }, [minLevel, maxLevel]);

  return (
    <Grid container size={12} spacing={1} {...props}>
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <PositionDropdown sx={{ mb: 0 }} source={`${source}.positionId`} label="Position" />
      </Grid>
      {
        // eslint-disable-next-line eqeqeq
        (get(data, `${source}.positionId`) == SOFTWARE_ENGINEER_ID ||
          // eslint-disable-next-line eqeqeq
          get(data, `${source}.positionId`) == EDUCATION_SPECIALIST_ID) && (
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <TechnologyDropdown
              sx={{ mb: 0 }}
              source={`${source}.technologyId`}
              label="Technology"
              placeholder="Choose Technology..."
            />
          </Grid>
        )
      }
      <Grid size={{ xs: 12, md: 6, lg: 3 }}>
        <EnumDropdown sx={{ mb: 0 }} options={PROJECT_MEMBERSHIP_ROLES} source={`${source}.teamRole`} label={'Role'} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 1.5 }}>
        <EnumDropdown
          sx={{ mb: 0 }}
          options={COMPETENCY_LEVEL_WITHOUT_NA}
          label={'Min Level'}
          source={`${source}.minLevel`}
          errorMessages={levelErrors.minLevel}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 1.5 }}>
        <EnumDropdown
          sx={{ mb: 0 }}
          options={COMPETENCY_LEVEL_WITHOUT_NA}
          label={'Max Level'}
          source={`${source}.maxLevel`}
          errorMessages={levelErrors.maxLevel}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6, lg: 3, xl: 1.5 }}>
        <NumberInput
          source={`${source}.requiredFte`}
          sx={{ mb: 0 }}
          label="FTE, %"
          slotProps={{
            htmlInput: {
              min: 0,
              step: 25,
            },
          }}
        />
      </Grid>
      {get(data, `${source}.qualification`) && (
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <EnumDropdown
            options={SIMPLE_QUALIFICATION}
            source={`${source}.qualification`}
            label="Qualification"
            disabled
            placeholder="Select..."
            sx={{ mb: 0 }}
          />
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 12, lg: 6 }}>
        <TextInput source={`${source}.noteText`} label="Note" />
      </Grid>
    </Grid>
  );
};

export default RequiredResourcesInputTemplate;
